import { ApiError, PROBLEM_TYPES } from "../api/client";
import { isRelease150 } from "./release150";

// O-R68 error wording (release 1.5.0 polish). Every failure a person sees is worded here, one pattern per kind: what
// happened (title), what to do (action), the server's own plain sentence when it is one the person can act on
// (detail), and the correlation reference for support. Server internals (exception names, stack lines, SQL, HTML,
// JSON, transport messages such as "Network Error") never reach the screen, and nothing from a 5xx answer is shown.
// With release 1.5.0 off, errorText returns exactly the 1.4.0 text so production does not change until 1.5.0 ships.

export type ErrorKind =
  | "offline"
  | "unreachable"
  | "signed-out"
  | "forbidden"
  | "not-found"
  | "conflict"
  | "changed"
  | "confirm"
  | "precondition"
  | "busy"
  | "server"
  | "invalid"
  | "refused"
  | "page";

export interface ErrorWording {
  kind: ErrorKind;
  /** What happened, one sentence. */
  title: string;
  /** What to do next, one or two sentences. */
  action: string;
  /** The server's own plain sentence, only when it is safe and useful; never for 5xx. */
  detail: string | null;
  /** The correlation id for support, when there is one and the person may need to quote it. */
  reference: string | null;
  status: number | null;
}

const WORDING: Record<ErrorKind, { title: string; action: string }> = {
  offline: { title: "You are offline.", action: "Check the network connection. The page tries again when you are back online." },
  unreachable: { title: "Summit Operations could not be reached.", action: "Check your connection and try again in a moment." },
  "signed-out": { title: "Your sign-in has ended.", action: "Sign in again, then repeat what you were doing." },
  forbidden: { title: "Your role does not include this.", action: "If you need it, ask an Admin for access." },
  "not-found": { title: "We could not find that.", action: "It may have been removed or the link is out of date. Go back and open it again from the list." },
  conflict: { title: "Someone else changed this at the same time.", action: "Reload to see the latest, then try again." },
  changed: { title: "This changed since you opened it.", action: "Reload to see the latest, then make your change again." },
  confirm: { title: "Not signed: you did not confirm it was you, so nothing was changed.", action: "Try again and enter the code from your authenticator app." },
  precondition: { title: "Nothing was changed.", action: "Reload and try again." },
  busy: { title: "Too many requests just now.", action: "Wait a minute, then try again." },
  server: { title: "Something went wrong on our side.", action: "Try again in a minute. If it keeps happening, send the reference to support." },
  invalid: { title: "That could not be saved as it is.", action: "Correct it and try again." },
  refused: { title: "That did not work.", action: "Try again. If it keeps happening, send the reference to support." },
  page: { title: "Something went wrong on this page.", action: "Reload the page and try again." }
};

// Things a person should never read: exception and type names, stack lines, SQL, markup, JSON, transport messages.
const TECHNICAL =
  /exception|stack ?trace|\bat [\w$]+(\.[\w$<>`]+)+\(|System\.[A-Z]|Microsoft\.[A-Z]|\bSQL|sqlite|Npgsql|Object reference|Sequence contains|NullReference|Traceback|TypeError|ReferenceError|SyntaxError|is not a function|Cannot read propert|undefined|<\/?[a-z][\s\S]*>|^\s*[{[]|Network Error|ECONN|ETIMEDOUT|ENOTFOUND|timeout of \d+ms|status code \d{3}|Request failed|\bHTTP\/\d/i;

/** True when a server sentence is plain words a person can act on. */
export function isSafeDetail(text: string | null | undefined): text is string {
  if (!text) return false;
  const t = text.trim();
  if (t.length === 0 || t.length > 300) return false;
  return !TECHNICAL.test(t);
}

function isOnline(online: boolean | undefined): boolean {
  if (typeof online === "boolean") return online;
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}

function kindOf(error: ApiError, online: boolean): ErrorKind {
  const s = error.status;
  if (s === 0) return online ? "unreachable" : "offline";
  if (s === 401) return "signed-out";
  if (s === 403) return "forbidden";
  if (s === 404 || s === 410) return "not-found";
  if (s === 409) return "conflict";
  if (s === 412) return "changed";
  if (s === 428) {
    if (error.type === PROBLEM_TYPES.stepUpRequired) return "confirm";
    if (error.type === PROBLEM_TYPES.staleWrite) return "changed";
    return "precondition";
  }
  if (s === 429) return "busy";
  if (s >= 500) return "server";
  if (s === 400 || s === 422) return "invalid";
  return "refused";
}

/** The plain wording of any error: an API refusal, a lost connection, or a fault in the page itself. */
export function describeError(error: unknown, options: { online?: boolean } = {}): ErrorWording {
  const online = isOnline(options.online);
  if (!(error instanceof ApiError)) {
    // A request that never left the browser while offline; anything else thrown in the page is a page fault.
    const kind: ErrorKind = !online ? "offline" : "page";
    return { kind, ...WORDING[kind], detail: null, reference: null, status: null };
  }
  const kind = kindOf(error, online);
  // Only refusals the person can act on keep the server's sentence; 5xx, transport and sign-in failures never do.
  const mayShowDetail = kind === "invalid" || kind === "conflict" || kind === "not-found" || kind === "refused" || kind === "forbidden";
  const candidate = error.message && error.message !== error.title ? error.message : null;
  const detail = mayShowDetail && isSafeDetail(candidate) ? candidate.trim() : null;
  // A validation refusal is fixed by the person; every other failure keeps its reference for support.
  const reference = kind === "invalid" && detail ? null : error.correlationId;
  return { kind, ...WORDING[kind], detail, reference: reference ?? null, status: error.status };
}

function sentence(text: string): string {
  const t = text.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

// A lead that says nothing beyond "it failed" is dropped: the title already says what happened.
const GENERIC_LEAD = /^(something went wrong|that did not work|an error occurred|error)\b/i;

/**
 * One line for inline messages: what the person tried (when given), what happened, what to do, and the reference.
 * When the server sent a plain sentence it replaces the generic title, so the person reads the actual reason.
 */
export function errorSentence(error: unknown, lead?: string, options: { online?: boolean } = {}): string {
  const w = describeError(error, options);
  const parts = [lead && !GENERIC_LEAD.test(lead.trim()) ? sentence(lead) : null, w.detail ? sentence(w.detail) : w.title, w.action, w.reference ? `Reference: ${w.reference}.` : null];
  return parts.filter(Boolean).join(" ");
}

/**
 * How a call site worded an error in 1.4.0: "api" = the API's detail, else the fallback; "api-or-title" = the detail or
 * the title; "error" = any Error's message.
 */
export type LegacyErrorText = "api" | "api-or-title" | "error";

function legacyText(error: unknown, fallback: string, legacy: LegacyErrorText): string {
  if (legacy === "error") return error instanceof Error ? error.message : fallback;
  if (!(error instanceof ApiError)) return fallback;
  return legacy === "api-or-title" ? error.message || error.title : error.message;
}

/** The text a page shows for a failed action: 1.4.0 wording with release 1.5.0 off, the plain O-R68 sentence with it on. */
export function errorText(error: unknown, fallback: string, legacy: LegacyErrorText = "api"): string {
  if (!isRelease150()) return legacyText(error, fallback, legacy);
  return errorSentence(error, fallback);
}
